import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import * as yup from 'yup';
import { WizardContent } from '../../components/WizardContent';
import { WizardTitle } from '../../components/WizardTitle';
import WizardButton from '../../components/WizardButton';
import WizardTextField from '../../components/WizardTextField';
import IFormError from '../../../../interfaces/IFormError';
import { Form, Link } from './ProfessionalData.styles';
import WizardSelect from '../../components/WizardSelect';
import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import IProviderService from '../../../../interfaces/IProviderService';
import IProfessionalType from '../../../../interfaces/IProfessionalType';
import { useLocalStorage } from '../../../../hooks/providers/useLocalStorage';
import ITypeDocument from '../../../../interfaces/ITypeDocument';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';
import IProviderServiceDataState from '../../../../interfaces/IProviderServiceDataState';
import ReactInputMask from 'react-input-mask'; // Importação da biblioteca

interface ProfessionalDataProps extends IProviderService {}

const ProfessionalData = (props: ProfessionalDataProps) => {
  const [stateProfessional, setStateSelected] = useState('');
  const [registerNumber, setRegisterNumber] = useState('');
  const [professionalTypeSelected, setProfessionalTypeSelected] = useState('');
  const [professionalName, setProfessionalName] = useState('');

  const [professionalTypes, setProfessionalTypes] = useState<IProfessionalType[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [errors, setErrors] = useState<IFormError>();
  const { listProfessionalTypes } = useWizardRequest();
  const validationProvader = useValidateSchema();
  const localStorage = useLocalStorage();
  const [documentType, setDocumentType] = useState<ITypeDocument | null>(null);
  const toastr = useToastrVanila();

  const schema = yup.object({
    stateProfessional: yup
      .string()
      .test((stateProfessional, schema) => stateValidation(`${stateProfessional}`, schema)),
    professionalTypeSelected: yup
      .string()
      .test((professionalTypeSelected, schema) => professionalTypeValidation(`${professionalTypeSelected}`, schema)),
    registerNumber: yup
      .number()
      .typeError('Por favor, insira apenas números neste campo. Outros caracteres não são permitidos.')
      .test((registerNumber, schema) => registerNumberValidation(`${registerNumber}`, schema)),
    professionalName: yup.string().test((professionalName, schema) => nameValidation(`${professionalName}`, schema)),
  });

  const stateValidation = (stateProfessional: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!stateProfessional) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (stateProfessional === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }
    return true;
  };

  const professionalTypeValidation = (
    professionalTypeSelected: string,
    schema: yup.TestContext,
  ): yup.ValidationError | boolean => {
    if (!professionalTypeSelected) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (professionalTypeSelected === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }
    return true;
  };

  const registerNumberValidation = (registerNumber: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!registerNumber) {
      return schema.createError({ message: `Campo obrigatório` });
    }

    return true;
  };

  const nameValidation = (professionalName: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!professionalName) {
      return schema.createError({ message: `Campo obrigatório` });
    }

    if (professionalName === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }

    return true;
  };

  const setState = (stateProfessional: string) => {
    setStateSelected(stateProfessional);
  };

  const loadProfessionalTypes = async () => {
    await listProfessionalTypes()
      .then(response => {
        setProfessionalTypes(response.data);
        const providerServiceDataState: IProviderServiceDataState = localStorage.getItem('providerServiceDataState');
        if (providerServiceDataState.providerServicePageSubmit === 2) {
          const name = response.data.find(type => type.id === props.medicalCertificate.professionalTypeId)?.name || '';
          setProfessionalTypeSelected(name);
        }
      })
      .catch(() => {
        toastr.error('Ocorreu um erro ao carregar os tipos de profissionais.');
      });
  };

  useEffect(() => {
    const documentType: ITypeDocument = localStorage.getItem('documentType');
    setDocumentType(documentType);
  }, []);

  const advanceWhithoutData = async () => {
    await props
      .setStep(props.step + 1, { ...props.medicalCertificate })
      .then(() => {})
      .catch(error => {
        toastr.error(error.message);
      });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = await validationProvader.validate(schema, {
      stateProfessional,
      professionalTypeSelected,
      registerNumber,
      professionalName,
    });
    setErrors({ ...result.errors });
    if (!result.isValid) {
      return;
    }

    const providerServiceDataState: IProviderServiceDataState = localStorage.getItem('providerServiceDataState');
    const providerServicePageSubmit = 2;

    const professionalTypeId = professionalTypes.find(
      type => !!professionalTypeSelected && type.name === professionalTypeSelected && type,
    )?.id;

    localStorage.setItem('providerServiceDataState', { ...providerServiceDataState, providerServicePageSubmit });
    const professionalType: IProfessionalType | undefined = professionalTypes.find(
      p => p.name === professionalTypeSelected,
    );

    await props.setStep(props.step + 1, {
      ...props.medicalCertificate,
      professionalState: stateProfessional,
      professionalName,
      registerNumber,
      professionalTypeId: professionalTypeId || 0,
      professionalTypeName: `${professionalType?.name}`,
      professionalTypeOrganization: `${professionalType?.organization}`,
    });
  };

  const loadData = async () => {
    setStates(props.states);

    setStateSelected(props.medicalCertificate.professionalState);
    setRegisterNumber(props.medicalCertificate.registerNumber);
    setProfessionalName(props.medicalCertificate.professionalName);
    setProfessionalTypeSelected(props.medicalCertificate.professionalTypeName);
  };

  useEffect(() => {
    loadProfessionalTypes();
    loadData();
  }, []);

  return (
    <WizardContent>
      <WizardTitle>Informe os dados do Profissional:</WizardTitle>
      <Form onSubmit={handleSubmit}>
        <WizardSelect
          setValueSelected={setState}
          label="Selecione o estado"
          error={Boolean(errors?.stateProfessional)}
          helperText={errors?.registerNumber}
          defaultValueSelected={stateProfessional}
        >
          {states.map(uf => {
            return (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            );
          })}
        </WizardSelect>

        <WizardSelect
          setValueSelected={setProfessionalTypeSelected}
          label="Tipo de Profissional"
          error={Boolean(errors?.professionalTypeSelected)}
          helperText={errors?.professionalTypeSelected}
          defaultValueSelected={professionalTypeSelected}
        >
          {professionalTypes && professionalTypes.length > 0 ? (
            professionalTypes
              .filter(profissionalType => profissionalType.name !== 'Médico')
              .map(professionalTypeSelected => {
                return (
                  <MenuItem key={professionalTypeSelected.name} value={professionalTypeSelected.name}>
                    {professionalTypeSelected.name}
                  </MenuItem>
                );
              })
          ) : (
            <MenuItem key={1} value={''}>
              {'Nenhum registro encontrato'}
            </MenuItem>
          )}
        </WizardSelect>

        <WizardTextField
          type={professionalTypeSelected === 'Laboratório' ? 'text' : 'number'}
          inputMode={professionalTypeSelected === 'Laboratório' ? 'text' : 'numeric'}
          label={professionalTypeSelected === 'Laboratório' ? 'CNPJ' : 'N° de registro'}
          value={professionalTypeSelected === 'Laboratório' ? String(registerNumber || '') : registerNumber}
          onChange={e => {
            setRegisterNumber(
              e.target.value
                .replaceAll('/', '')
                .replaceAll('.', '')
                .replaceAll('-', ''),
            );
          }}
          InputProps={{
            inputComponent: professionalTypeSelected === 'Laboratório' ? (ReactInputMask as any) : undefined,
            inputProps: professionalTypeSelected === 'Laboratório' ? { mask: '99.999.999/9999-99' } : {},
          }}
          sx={{ marginBottom: '20px', width: '300px' }}
          variant="outlined"
          error={Boolean(errors?.registerNumber)}
          helperText={errors?.registerNumber}
        />

        <WizardTextField
          label="Nome do profissional"
          value={professionalName}
          onChange={e => {
            setProfessionalName(e.target.value);
          }}
          sx={{ marginBottom: '20px', width: '300px' }}
          variant="outlined"
          error={Boolean(errors?.professionalName)}
          helperText={errors?.professionalName}
        />

        <Link type="button" onClick={() => props.setCurrentPage(1)}>
          Voltar
        </Link>

        {!documentType?.crmRequired && (
          <Link type="button" onClick={advanceWhithoutData}>
            Não tenho esses dados
          </Link>
        )}

        <WizardButton type="submit" isLoading={false} disabled={false} sx={{ marginTop: '35px' }}>
          PRÓXIMO
        </WizardButton>
      </Form>
    </WizardContent>
  );
};
export default ProfessionalData;
